import React, { useId, useMemo, useRef, useState } from "react";
import { normalizeTurkish, turkishIncludes } from "../utils/formatters";
import "./LookupField.css";

/**
 * po-* dilinde küçük bir arama-seç alanı (İl / İlçe gibi sabit listeler için).
 * Kontrollü çalışır: metin (`text`) ve seçili kimlik (`value`) üst bileşende durur.
 * Davranış: tam eşleşen bir ad yazılınca seçer; geçerli bir seçim olmadan odak
 * kaybında metni temizler; ↑/↓/Enter/Esc klavye desteği; Türkçe duyarsız arama.
 */
export interface LookupOption {
  id: string;
  label: string;
}

interface Props {
  label: React.ReactNode;
  options: LookupOption[];
  /** Seçili kimlik ("" = seçim yok). */
  value: string;
  /** Giriş kutusundaki metin. */
  text: string;
  onTextChange: (text: string) => void;
  /** Seçim değiştiğinde; temizlemede ("", "") gelir. */
  onChange: (id: string, label: string) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyText?: string;
  /** Listede en çok kaç sonuç gösterilsin. */
  limit?: number;
}

const checkIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>;
const searchIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
const closeIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>;

const sameName = (a: string, b: string) => normalizeTurkish(a || "") === normalizeTurkish(b || "");

const LookupField: React.FC<Props> = ({
  label, options, value, text, onTextChange, onChange,
  disabled, placeholder, emptyText = "Sonuç bulunamadı", limit = 10,
}) => {
  const uid = useId();
  const listId = `lk-list-${uid}`;
  const labelId = `lk-label-${uid}`;
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(
    () => options.filter((o) => !text || turkishIncludes(o.label, text)).slice(0, limit),
    [options, text, limit],
  );

  const pick = (o: LookupOption) => {
    onTextChange(o.label);
    onChange(o.id, o.label);
    setOpen(false);
    setHi(-1);
  };

  const clear = () => {
    onTextChange("");
    onChange("", "");
    setOpen(false);
    setHi(-1);
    inputRef.current?.focus();
  };

  const handleText = (v: string) => {
    onTextChange(v);
    setOpen(true);
    setHi(-1);
    const exact = options.find((o) => sameName(o.label, v));
    if (exact) onChange(exact.id, exact.label);
    else if (!v) onChange("", "");
  };

  const handleBlur = () => {
    setOpen(false);
    setHi(-1);
    // Geçerli bir seçim yoksa yazılanı bırakma
    if (text && !options.some((o) => sameName(o.label, text))) {
      onTextChange("");
      onChange("", "");
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); setHi(0); return; }
      setHi((h) => (matches.length ? (h + 1) % matches.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); setHi(matches.length - 1); return; }
      setHi((h) => (matches.length ? (h <= 0 ? matches.length - 1 : h - 1) : -1));
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      const target = hi >= 0 ? matches[hi] : matches.length === 1 ? matches[0] : undefined;
      if (target) pick(target);
    } else if (e.key === "Escape") {
      if (!open) return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setHi(-1);
    }
  };

  return (
    <div className="po-field lk-field" data-lookup-open={open ? "true" : undefined}>
      <span id={labelId}>{label}</span>
      <div className="lk">
        <div className="lk-in">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-labelledby={labelId}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && hi >= 0 && matches[hi] ? `${listId}-${matches[hi].id}` : undefined}
            autoComplete="off"
            className={value ? "is-on" : ""}
            value={text}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => handleText(e.target.value)}
            onFocus={() => { if (!disabled) setOpen(true); }}
            onBlur={handleBlur}
            onKeyDown={handleKey}
          />
          {text && !disabled && (
            <button
              type="button"
              className="po-ib lk-clear"
              aria-label="Temizle"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={clear}
            >
              {closeIcon}
            </button>
          )}
          <span className={`lk-ic ${value ? "ok" : ""}`} aria-hidden="true">{value ? checkIcon : searchIcon}</span>
        </div>
        {open && !disabled && (
          <ul id={listId} className="lk-list" role="listbox" onMouseDown={(e) => e.preventDefault()}>
            {matches.map((o, i) => (
              <li
                key={o.id}
                id={`${listId}-${o.id}`}
                role="option"
                aria-selected={o.id === value}
                className={`lk-opt${i === hi ? " hi" : ""}${o.id === value ? " on" : ""}`}
                onMouseEnter={() => setHi(i)}
                onClick={() => pick(o)}
              >
                <span>{o.label}</span>
                {o.id === value && checkIcon}
              </li>
            ))}
            {matches.length === 0 && <li className="lk-empty">{emptyText}</li>}
          </ul>
        )}
      </div>
    </div>
  );
};

export default LookupField;
